import React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { vi } from 'date-fns/locale';
import { format } from 'date-fns';
import { NumericFormat } from 'react-number-format';
import { Cancel as CancelIcon, Save as SaveIcon } from '@mui/icons-material';
import LabeledField from '@/__core/components/LabeledField';
import { sharedFormStyles } from '@/__core/styles/sharedFormStyles';

export type AppFormOption = {
  value: string;
  label: string;
  [key: string]: any;
};

type GridSize = { xs?: number; sm?: number; md?: number; lg?: number; xl?: number };

type AppFormFieldBase = {
  key: string;
  label: string;
  type: 'text' | 'numeric' | 'select' | 'autocomplete' | 'date' | 'custom';
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  labelWidth?: string | number;
  gridSize?: GridSize;
  visible?: boolean;
  error?: boolean;
  helperText?: string;
};

type AppTextField = AppFormFieldBase & {
  type: 'text';
  multiline?: boolean;
  rows?: number;
};

type AppNumericField = AppFormFieldBase & {
  type: 'numeric';
  decimalScale?: number;
  thousandSeparator?: string;
  decimalSeparator?: string;
  allowNegative?: boolean;
};

type AppSelectField = AppFormFieldBase & {
  type: 'select';
  options: AppFormOption[];
};

type AppAutocompleteField = AppFormFieldBase & {
  type: 'autocomplete';
  options: any[];
  loading?: boolean;
  noOptionsText?: string;
  getOptionLabel?: (option: any) => string;
  isOptionEqualToValue?: (option: any, value: any) => boolean;
  mapValueToOption?: (value: any, options: any[]) => any;
  mapOptionToValue?: (option: any | null) => any;
  filterOptions?: (options: any[], state: { inputValue: string }) => any[];
  renderOption?: (props: any, option: any) => React.ReactNode;
};

type AppDateField = AppFormFieldBase & {
  type: 'date';
  displayFormat?: string;
  parseValue?: (value: any) => Date | null;
  formatValue?: (value: Date | null) => any;
};

type AppCustomField = AppFormFieldBase & {
  type: 'custom';
  standalone?: boolean;
  render: (context: { values: Record<string, any>; onChange: (key: string, value: any) => void }) => React.ReactNode;
};

export type AppFormField =
  | AppTextField
  | AppNumericField
  | AppSelectField
  | AppAutocompleteField
  | AppDateField
  | AppCustomField;

export interface AppFormGroup {
  gridSize?: GridSize;
  fields: string[];
}

export interface AppFormSection {
  key: string;
  title?: string;
  fields: AppFormField[];
  groups?: AppFormGroup[];
  cardSx?: any;
  disablePaper?: boolean;
}

interface AppFormProps {
  sections?: AppFormSection[];
  values?: Record<string, any>;
  onChange?: (key: string, value: any) => void;
  onSubmit: () => void;
  onCancel: () => void;
  submitting?: boolean;
  submitDisabled?: boolean;
  submitLabel?: string;
  submittingLabel?: string;
  cancelLabel?: string;
  cancelDisabled?: boolean;
  cancelStartIcon?: React.ReactNode;
  cancelButtonSx?: any;
  bottomContent?: React.ReactNode;
  children?: React.ReactNode;
  containerSx?: any;
  actionBarSx?: any;
  hideActionBar?: boolean;
  /** When false, only cancel/close is shown (e.g. read-only detail view). */
  showSubmitButton?: boolean;
}

const defaultParseDate = (value: any): Date | null => {
  if (!value || typeof value !== 'string') return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
};

const defaultFormatDate = (value: Date | null): string => {
  if (!value) return '';
  return format(value, 'yyyy-MM-dd');
};

const defaultOptionLabel = (option: any): string => {
  if (!option) return '';
  return String(option.label || option.name || option.value || option.id || '');
};

const defaultMapValueToOption = (value: any, options: any[]) => {
  return options.find((option) => String(option.id ?? option.value) === String(value)) || null;
};

const defaultMapOptionToValue = (option: any | null) => {
  if (!option) return '';
  return option.id ?? option.value ?? '';
};

const parseNumericInput = (rawValue: string, thousandSep: string, decimalSep: string): number => {
  if (!rawValue) return 0;

  const decimalIndex = decimalSep ? rawValue.lastIndexOf(decimalSep) : -1;
  let integerPart = decimalIndex !== -1 ? rawValue.substring(0, decimalIndex) : rawValue;
  let decimalPart = decimalIndex !== -1 ? rawValue.substring(decimalIndex + 1) : '';

  if (thousandSep) {
    const escaped = thousandSep.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    integerPart = integerPart.replace(new RegExp(escaped, 'g'), '');
  }

  const cleaned = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

export default function AppForm({
  sections = [],
  values,
  onChange,
  onSubmit,
  onCancel,
  submitting = false,
  submitDisabled = false,
  submitLabel = 'Lưu',
  submittingLabel = 'Đang lưu...',
  cancelLabel = 'Hủy',
  cancelDisabled = false,
  cancelStartIcon,
  cancelButtonSx,
  bottomContent,
  children,
  containerSx,
  actionBarSx,
  hideActionBar = false,
  showSubmitButton = true,
}: AppFormProps) {
  const safeValues = values || {};
  const safeOnChange = onChange || (() => {});

  const renderField = (field: AppFormField) => {
    if (field.visible === false) return null;

    if (field.type === 'custom') {
      if (field.standalone) {
        return <>{field.render({ values: safeValues, onChange: safeOnChange })}</>;
      }

      return (
        <LabeledField
          label={field.label}
          required={field.required}
          labelWidth={field.labelWidth || 100}
          error={field.error}
          helperText={field.helperText}
        >
          {field.render({ values: safeValues, onChange: safeOnChange })}
        </LabeledField>
      );
    }

    if (field.type === 'text') {
      return (
        <LabeledField
          label={field.label}
          required={field.required}
          labelWidth={field.labelWidth || 100}
          error={field.error}
          helperText={field.helperText}
        >
          <TextField
            fullWidth
            size='small'
            value={safeValues[field.key] || ''}
            onChange={(event) => safeOnChange(field.key, event.target.value)}
            disabled={field.disabled}
            placeholder={field.placeholder}
            error={field.error}
            multiline={field.multiline}
            rows={field.rows}
          />
        </LabeledField>
      );
    }

    if (field.type === 'numeric') {
      return (
        <LabeledField
          label={field.label}
          required={field.required}
          labelWidth={field.labelWidth || 100}
          error={field.error}
          helperText={field.helperText}
        >
          <NumericFormat
            value={safeValues[field.key] ?? 0}
            decimalScale={field.decimalScale ?? 0}
            thousandSeparator={field.thousandSeparator ?? '.'}
            decimalSeparator={field.decimalSeparator ?? ','}
            allowNegative={field.allowNegative ?? false}
            customInput={TextField}
            size='small'
            fullWidth
            onChange={(event: any) => {
              const rawValue = event.target.value;
              const thousandSep = field.thousandSeparator ?? '.';
              const decimalSep = field.decimalSeparator ?? ',';
              const value = parseNumericInput(rawValue, thousandSep, decimalSep);
              safeOnChange(field.key, value);
            }}
            disabled={field.disabled}
            placeholder={field.placeholder}
          />
        </LabeledField>
      );
    }

    if (field.type === 'select') {
      return (
        <LabeledField
          label={field.label}
          required={field.required}
          labelWidth={field.labelWidth || 100}
          error={field.error}
          helperText={field.helperText}
        >
          <FormControl fullWidth size='small'>
            <Select
              value={safeValues[field.key] ?? ''}
              onChange={(event) => safeOnChange(field.key, event.target.value)}
              disabled={field.disabled}
              sx={sharedFormStyles.inputArrowAlign}
            >
              {field.options.map((option) => (
                <MenuItem key={String(option.value)} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </LabeledField>
      );
    }

    if (field.type === 'autocomplete') {
      const getOptionLabel = field.getOptionLabel || defaultOptionLabel;
      const mapValueToOption = field.mapValueToOption || defaultMapValueToOption;
      const mapOptionToValue = field.mapOptionToValue || defaultMapOptionToValue;
      const value = mapValueToOption(safeValues[field.key], field.options);

      return (
        <LabeledField
          label={field.label}
          required={field.required}
          labelWidth={field.labelWidth || 100}
          error={field.error}
          helperText={field.helperText}
        >
          <Autocomplete
            fullWidth
            size='small'
            sx={sharedFormStyles.inputArrowAlign}
            options={field.options}
            value={value}
            onChange={(_, nextValue) => safeOnChange(field.key, mapOptionToValue(nextValue))}
            getOptionLabel={getOptionLabel}
            isOptionEqualToValue={field.isOptionEqualToValue}
            filterOptions={field.filterOptions}
            loading={field.loading}
            disabled={field.disabled}
            noOptionsText={field.noOptionsText}
            renderOption={field.renderOption}
            renderInput={(params) => (
              <TextField
                {...params}
                size='small'
                fullWidth
                placeholder={field.placeholder}
              />
            )}
          />
        </LabeledField>
      );
    }

    const parseValue = field.parseValue || defaultParseDate;
    const formatValue = field.formatValue || defaultFormatDate;

return (
        <LabeledField
          label={field.label}
          required={field.required}
          labelWidth={field.labelWidth === 0 ? 0 : (field.labelWidth || 100)}
          error={field.error}
          helperText={field.helperText}
        >
        <DatePicker
          value={parseValue(safeValues[field.key])}
          onChange={(date) => safeOnChange(field.key, formatValue(date))}
          disabled={field.disabled}
          format={field.displayFormat || 'dd/MM/yyyy'}
          slotProps={{
            textField: {
              size: 'small',
              fullWidth: true,
              placeholder: field.placeholder,
              sx: sharedFormStyles.datePickerArrowAlign,
            },
          }}
        />
      </LabeledField>
    );
  };

  return (
    <Box
      sx={{
        ...sharedFormStyles.pageContainer,
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
        boxSizing: 'border-box',
        overflowX: 'hidden',
        ...(containerSx || {}),
      }}
    >
      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, width: '100%', minWidth: 0, overflowX: 'hidden' }}>
          {children ? (
            <Box sx={{ '& > *:last-child': { mb: '0 !important' } }}>
              {children}
            </Box>
          ) : sections.map((section, index) => {
            const sectionContent = (
              <>
                {section.title ? (
                  <Typography sx={sharedFormStyles.sectionTitle}>{section.title}</Typography>
                ) : null}

                <Grid container rowSpacing={1} columnSpacing={5} sx={{ width: '100%', m: 0, minWidth: 0 }}>
                  {section.groups && section.groups.length > 0 ? (
                    section.groups.map((group, groupIndex) => (
                      <Grid key={groupIndex} size={group.gridSize || { xs: 12 }} sx={{ minWidth: 0 }}>
                        {section.fields
                          .filter((field) => group.fields.includes(field.key))
                          .map((field) => (
                            <Box key={field.key}>{renderField(field)}</Box>
                          ))}
                      </Grid>
                    ))
                  ) : (
                    section.fields.map((field) => (
                      <Grid
                        key={field.key}
                        size={field.gridSize || { xs: 12 }}
                        sx={{ minWidth: 0 }}
                      >
                        {renderField(field)}
                      </Grid>
                    ))
                  )}
                </Grid>

                {bottomContent}
              </>
            );

            if (section.disablePaper) {
              return (
                <Box
                  key={section.key}
                  sx={{
                    mb: index === sections.length - 1 ? 0 : 2,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    boxSizing: 'border-box',
                    overflowX: 'hidden',
                    ...(section.cardSx || {}),
                  }}
                >
                  {sectionContent}
                </Box>
              );
            }

            return (
              <Paper
                key={section.key}
                elevation={0}
                sx={{
                  ...sharedFormStyles.formCard,
                  mb: index === sections.length - 1 ? 0 : 2,
                  width: '100%',
                  maxWidth: '100%',
                  minWidth: 0,
                  boxSizing: 'border-box',
                  overflowX: 'hidden',
                  ...(section.cardSx || {}),
                }}
              >
                {sectionContent}
              </Paper>
            );
          })}

          {!hideActionBar && (
            <Box
              sx={{
                ...sharedFormStyles.actionBar,
                mt: 2,
                width: '100%',
                boxSizing: 'border-box',
                minWidth: 0,
                ...(actionBarSx || {}),
              }}
            >
              <Button
                variant='contained'
                size='small'
                startIcon={cancelStartIcon || <CancelIcon />}
                onClick={onCancel}
                disabled={submitting || cancelDisabled}
                sx={{ ...sharedFormStyles.cancelButton, mt: '0 !important', '&.MuiButton-root': { mt: '0 !important' }, ...(cancelButtonSx || {}) }}
              >
                {cancelLabel}
              </Button>

              {showSubmitButton && (
                <Button
                  variant='contained'
                  size='small'
                  startIcon={submitting ? <CircularProgress size={20} color='inherit' /> : <SaveIcon />}
                  onClick={onSubmit}
                  disabled={submitting || submitDisabled}
                  sx={{ ...sharedFormStyles.primaryButton, mt: '0 !important', '&.MuiButton-root': { mt: '0 !important' } }}
                >
                  {submitting ? submittingLabel : submitLabel}
                </Button>
              )}
            </Box>
          )}
        </Box>
      </LocalizationProvider>
    </Box>
  );
}
